import { isString, present } from '@art-suite/art-core-ts';

import { NonNullableScanValueType, ScanValueType } from '../CommonScanSchemas';
import { SetValues, ValueOrExpression, ValueType } from '../OptimizedValueExpressions';
import { ScanValue, ScanValues } from './ScanRootSchemas';

/** setValue (a user override) wins over calculatedValue; undefined when neither is set. */
export const getEffectiveScanValue = (value?: ScanValue): NonNullableScanValueType | undefined => value?.setValue ?? value?.calculatedValue ?? undefined;

/** getEffectiveScanValue except will return undefined unless the result is a number. */
export const getEffectiveScanValueAsNumber = (valueObject?: ScanValue): number | undefined => {
  const value = getEffectiveScanValue(valueObject);
  return typeof value === 'number' ? value : undefined;
};

const EMPTY_SET_VALUES: SetValues = {};
export const getSetValuesFromScanValues = (values: ScanValues | undefined): SetValues => {
  if (!values) return EMPTY_SET_VALUES;
  const setValues: SetValues = {};
  for (const key in values) {
    const expressionValue = getValueTypeFromScanValue(values[key].setValue);
    if (expressionValue != null) setValues[key] = expressionValue;
  }
  return setValues;
};

export const getValueTypeFromScanValue = (value: ScanValueType | undefined): ValueType | undefined =>
  typeof value === 'number'
    ? value
    : typeof value === 'boolean'
    ? value ? 1 : 0
    : undefined;

export const getValueOrExpressionFromScanValue = (value: ScanValueType | undefined): ValueOrExpression | undefined =>
  typeof value === 'boolean'
    ? value ? 1 : 0
    : value ?? undefined;

/**
 * Best-effort coercion of a scan value to a boolean; it never throws. Numbers are truthy except 0, the string
 * 'false' is false and any other non-blank string is true. Absent input (`undefined`, `null` or a blank
 * string) is `undefined`. Checkbox values are stored this way in both `calculatedValue` and `setValue`,
 * even though the calculation engine computes them as 1/0.
 */
export const getBooleanScanValueOrUndefined = (value: ScanValueType | undefined): boolean | undefined =>
  !present(value)
    ? undefined
    : isString(value)
    ? value !== 'false'
    : !!value;
